import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { ApiError } from '../../models/api-error.enum';
import type { Loadable } from '../../models/loadable.model';
import { emptyLoadable } from '../../models/loadable.model';
import { toastService } from '../../toast/toast.service';
import { windowNavigate } from '../../utils/window.utils';
import type { Membership, MembershipJoinRequest, ReferralLink } from '../models/membership.model';
import { MembershipView } from '../models/membership.model';
import {
  addMembershipToCart,
  cancelMembershipUpdate,
  createContract,
  downloadWalletPass,
  loadAddressData,
  loadMemberships,
  loadReferralLink,
  updateMembership,
  updateMembershipAddress,
  updatePaymentMethod,
} from './membership.thunk';

// ==================== STATE INTERFACE ====================

export interface IMembershipState {
  memberships: Loadable<Membership[]>;
  addressData: Loadable<string>;
  updatingAddress: Loadable<any>;
  updating: Loadable<any>;
  cancelling: Loadable<any>;
  creatingContract: Loadable<string | undefined>;
  addToCart: Loadable<string | undefined>;
  selectedMembership?: Membership;
  selectedView: MembershipView;
  referralLink: Loadable<ReferralLink>;
  membershipJoinRequest?: MembershipJoinRequest;
  // True while a join (post-auth auto-join or explicit click) is being processed.
  // Gated components show a waiting state instead of the access lock (SNGL-6967).
  joinInProgress: boolean;
}

export const initialState: IMembershipState = {
  memberships: emptyLoadable(),
  addressData: emptyLoadable(),
  updatingAddress: emptyLoadable(),
  updating: emptyLoadable(),
  cancelling: emptyLoadable(),
  creatingContract: emptyLoadable(),
  addToCart: emptyLoadable(),
  selectedMembership: undefined,
  selectedView: MembershipView.LIST,
  referralLink: emptyLoadable(),
  membershipJoinRequest: undefined,
  joinInProgress: false,
};

// ==================== SLICE ====================

const membershipSlice = createSlice({
  name: 'membership',
  initialState,
  reducers: {
    // Synchronous actions
    setMembershipView(state, action: PayloadAction<MembershipView>) {
      state.selectedView = action.payload;
    },

    setSelectedMembership(state, action: PayloadAction<Membership | undefined>) {
      state.selectedMembership = action.payload;
    },

    setMembershipJoinRequest(state, action: PayloadAction<MembershipJoinRequest | undefined>) {
      state.membershipJoinRequest = action.payload;
    },

    setJoinInProgress(state, action: PayloadAction<boolean>) {
      state.joinInProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ========== Load Memberships ==========
    builder
      .addCase(loadMemberships.pending, (state: IMembershipState) => {
        state.memberships.loading = true;
        state.memberships.loaded = false;
        state.memberships.error = undefined;
      })
      .addCase(loadMemberships.fulfilled, (state: IMembershipState, action) => {
        state.memberships.loading = false;
        state.memberships.loaded = true;
        state.memberships.value = action.payload;
        state.memberships.error = undefined;
      })
      .addCase(loadMemberships.rejected, (state: IMembershipState, action) => {
        state.memberships.loading = false;
        state.memberships.loaded = false;
        state.memberships.error = action.payload ?? ApiError.Other;
      });

    // ========== Load Address Data ==========
    builder
      .addCase(loadAddressData.pending, (state: IMembershipState) => {
        state.addressData.loading = true;
        state.addressData.loaded = false;
        state.addressData.error = undefined;
      })
      .addCase(loadAddressData.fulfilled, (state: IMembershipState, action) => {
        state.addressData.loading = false;
        state.addressData.loaded = true;
        state.addressData.value = action.payload;
        state.addressData.error = undefined;
      })
      .addCase(loadAddressData.rejected, (state: IMembershipState, action) => {
        state.addressData.loading = false;
        state.addressData.loaded = false;
        state.addressData.value = undefined;
        state.addressData.error = action.payload ?? ApiError.Other;
      });

    // ========== Update Membership ==========
    builder
      .addCase(updateMembership.pending, (state: IMembershipState) => {
        state.updating.loading = true;
        state.updating.loaded = false;
        state.updating.error = undefined;
      })
      .addCase(updateMembership.fulfilled, (state: IMembershipState) => {
        // Membership reload triggered automatically in thunk
        state.updating.loading = false;
        state.updating.loaded = true;
        state.updating.error = undefined;
      })
      .addCase(updateMembership.rejected, (state: IMembershipState, action) => {
        state.updating.loading = false;
        state.updating.loaded = false;
        state.updating.error = action.payload ?? ApiError.Other;
      });

    // ========== Cancel Membership Update ==========
    builder
      .addCase(cancelMembershipUpdate.pending, (state: IMembershipState) => {
        state.cancelling.loading = true;
        state.cancelling.loaded = false;
        state.cancelling.error = undefined;
      })
      .addCase(cancelMembershipUpdate.fulfilled, (state: IMembershipState) => {
        // Membership reload triggered automatically in thunk
        state.cancelling.loading = false;
        state.cancelling.loaded = true;
        state.cancelling.error = undefined;
      })
      .addCase(cancelMembershipUpdate.rejected, (state: IMembershipState, action) => {
        state.cancelling.loading = false;
        state.cancelling.loaded = false;
        state.cancelling.error = action.payload ?? ApiError.Other;
      });

    // ========== Update Address ==========
    builder
      .addCase(updateMembershipAddress.pending, (state: IMembershipState) => {
        state.updatingAddress.loading = true;
        state.updatingAddress.loaded = false;
        state.updatingAddress.error = undefined;
      })
      .addCase(updateMembershipAddress.fulfilled, (state: IMembershipState) => {
        // Membership reload triggered automatically in thunk
        state.updatingAddress.loading = false;
        state.updatingAddress.loaded = true;
        state.updatingAddress.error = undefined;
        toastService.success('Success!', 'Address updated successfully.');
      })
      .addCase(updateMembershipAddress.rejected, (state: IMembershipState, action) => {
        state.updatingAddress.loading = false;
        state.updatingAddress.loaded = false;
        state.updatingAddress.error = action.payload ?? ApiError.Other;
        toastService.error('Error', 'Failed to update address. Please contact support.');
      });

    // ========== Update Payment Method ==========
    builder
      .addCase(updatePaymentMethod.pending, () => {
        // no-op
      })
      .addCase(updatePaymentMethod.fulfilled, () => {
        toastService.success(
          'Payment Update Email Sent',
          'Check your inbox for further instructions.'
        );
      })
      .addCase(updatePaymentMethod.rejected, () => {
        toastService.error(
          'Error',
          'Failed to request payment update email. Please contact support.'
        );
      });

    // ========== Create Contract ==========
    builder
      .addCase(createContract.pending, (state: IMembershipState) => {
        state.creatingContract.loading = true;
        state.creatingContract.loaded = false;
        state.creatingContract.error = undefined;
      })
      .addCase(createContract.fulfilled, (state: IMembershipState, action) => {
        state.creatingContract.loading = false;
        state.creatingContract.loaded = true;
        state.creatingContract.value = action.payload.redirectUrl;
        state.creatingContract.error = undefined;
      })
      .addCase(createContract.rejected, (state: IMembershipState, action) => {
        state.creatingContract.loading = false;
        state.creatingContract.loaded = true;
        state.creatingContract.value = undefined;
        state.creatingContract.error = action.payload ?? ApiError.Other;
      });

    // ========== Add to Cart ==========
    builder
      .addCase(addMembershipToCart.pending, (state: IMembershipState, action) => {
        state.addToCart.loading = true;
        state.addToCart.loaded = false;
        state.addToCart.value = action.meta.arg.tierId;
        state.addToCart.error = undefined;
      })
      .addCase(addMembershipToCart.fulfilled, (state: IMembershipState, action) => {
        state.addToCart.loading = false;
        state.addToCart.loaded = true;
        state.addToCart.value = action.meta.arg.tierId;
        state.addToCart.error = undefined;
      })
      .addCase(addMembershipToCart.rejected, (state: IMembershipState, action) => {
        state.addToCart.loading = false;
        state.addToCart.loaded = false;
        state.addToCart.value = undefined;
        state.addToCart.error = action.payload ?? ApiError.Other;
      });

    // ========== Load Referral Link ==========
    builder
      .addCase(loadReferralLink.pending, (state: IMembershipState) => {
        state.referralLink.loading = true;
        state.referralLink.loaded = false;
        state.referralLink.error = undefined;
      })
      .addCase(loadReferralLink.fulfilled, (state: IMembershipState, action) => {
        state.referralLink.loading = false;
        state.referralLink.loaded = true;
        state.referralLink.value = action.payload;
        state.referralLink.error = undefined;
      })
      .addCase(loadReferralLink.rejected, (state: IMembershipState, action) => {
        state.referralLink.loading = false;
        state.referralLink.loaded = false;
        state.referralLink.error = action.payload ?? ApiError.Other;
      });

    // ========== Download Wallet Pass ==========
    builder
      .addCase(downloadWalletPass.pending, () => {
        // no-op
      })
      .addCase(downloadWalletPass.fulfilled, (_state: IMembershipState, action) => {
        if (action.payload) {
          windowNavigate(action.payload);
        }
      })
      .addCase(downloadWalletPass.rejected, () => {
        toastService.error('Error', 'Unable to download membership wallet pass');
      });
  },
});

export const { setMembershipView, setSelectedMembership, setMembershipJoinRequest, setJoinInProgress } =
  membershipSlice.actions;
export const membershipReducer = membershipSlice.reducer;
